import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckoutService, StorefrontService } from "src/app/services/StorefrontService";
import { Basket, BasketLine, money } from "./Basket";
import { FanSession } from "./FanSession";
import { clubTheme } from "./ClubTheme";
import "./Shop.css";
import { ShopHeader } from "./ShopHeader";

/**
 * Stripe's SDK is loaded once per publishable key and cached.
 *
 * loadStripe injects a script tag, so calling it inside a render would add one
 * on every keystroke in the email field.
 */
const stripeCache: { [key: string]: Promise<Stripe | null> } = {};
const stripeFor = (key: string) => {
	if (!stripeCache[key]) stripeCache[key] = loadStripe(key);
	return stripeCache[key];
};

/**
 * The card step.
 *
 * A function component because Stripe's hooks require one — there is no class
 * API. It confirms in place where the card allows it, and only leaves the page
 * when the bank insists on its own screen.
 */
function PaymentForm({ orderId, orderKey, slug, totalPence }: { orderId: number; orderKey: string; slug: string; totalPence: number }) {
	const stripe = useStripe();
	const elements = useElements();
	const navigate = useNavigate();
	const [paying, setPaying] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);

	const pay = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!stripe || !elements) return;

		setPaying(true);
		setError(null);

		const result = await stripe.confirmPayment({
			confirmParams: {
				// Only used when the bank takes over the page. Everything else
				// lands on the same URL, so a fan who does get redirected ends
				// up exactly where a fan who doesn't would.
				return_url: `${window.location.origin}/club/${slug}/order/${orderId}?key=${encodeURIComponent(orderKey)}`
			},
			elements,
			redirect: "if_required"
		});

		if (result.error) {
			setPaying(false);
			setError(result.error.message || "That payment didn't go through.");
			return;
		}

		// The basket is done with; the order is the record now. Clearing here
		// rather than on the confirmation page means a fan who closes the tab
		// mid-3DS doesn't come back to a basket they've already paid for.
		Basket.clear(slug);
		navigate(`/club/${slug}/order/${orderId}?key=${encodeURIComponent(orderKey)}`);
	};

	return (
		<form onSubmit={pay}>
			{error && <div className="shop-error">{error}</div>}
			<PaymentElement />
			<div style={{ marginTop: 20 }}>
				<button className="shop-btn" disabled={!stripe || paying} type="submit">
					{paying ? "Taking payment…" : `Pay ${money(totalPence)}`}
				</button>
			</div>
			<p className="shop-note">Your card details go straight to Stripe. We never see them.</p>
		</form>
	);
}

/**
 * The basket, the buyer's details, and the payment.
 *
 * All on one page on purpose. A three-page checkout is where supporters fall
 * out, and there is nothing here that needs its own screen.
 *
 * The total shown is whatever the server's Quote says — never the sum of what
 * the basket has cached. If a club changed a price while somebody was
 * browsing, the number they're asked to approve is the number they'll be
 * charged.
 */
export function ShopBasket() {
	const { slug = "" } = useParams();
	const navigate = useNavigate();

	const [lines, setLines] = React.useState<BasketLine[]>(() => Basket.read(slug));
	const [club, setClub] = React.useState<any>(null);
	const [payment, setPayment] = React.useState<any>(null);
	const [quote, setQuote] = React.useState<any>(null);
	const [name, setName] = React.useState(() => {
		const p = FanSession.profile();
		return [p?.firstName, p?.lastName].filter(Boolean).join(" ");
	});
	const [email, setEmail] = React.useState(FanSession.profile()?.emailAddress || "");
	const [error, setError] = React.useState<string | null>(null);
	const [promo, setPromo] = React.useState("");
	const [applied, setApplied] = React.useState("");
	const [starting, setStarting] = React.useState(false);
	const [order, setOrder] = React.useState<{ orderId: number; orderKey: string; clientSecret: string; totalPence: number } | null>(null);

	React.useEffect(() => {
		let live = true;
		(async () => {
			try {
				const [shopRes, payRes] = await Promise.all([
					StorefrontService.get(slug),
					StorefrontService.getPaymentConfig(slug)
				]);
				if (live && shopRes.ok) setClub((await shopRes.json()).club);
				if (live && payRes.ok) setPayment(await payRes.json());
			} catch {
				// The basket still works; the pay button will say why it can't.
			}
		})();
		return () => { live = false; };
	}, [slug]);

	// Re-price whenever the basket changes. The server is the only thing that
	// knows what these lines are worth right now.
	React.useEffect(() => {
		let live = true;
		if (!lines.length) { setQuote(null); return; }
		(async () => {
			try {
				const res = await CheckoutService.quote({
					clubSlug: slug,
					fanId: FanSession.fanId(),
					lines: lines.map((l) => ({
						productId: l.productId,
						productType: l.productType,
						quantity: l.quantity,
						ticketTypeId: l.ticketTypeId || null
					})),
					promoCode: applied || null,
					ticketCategory: 0
				});
				if (!live) return;
				if (!res.ok) {
					const body = await res.text();
					setQuote(null);
					setError(body?.replace(/^"|"$/g, "") || "Something in your basket isn't available any more.");
					return;
				}
				setError(null);
				setQuote(await res.json());
			} catch {
				if (live) setQuote(null);
			}
		})();
		return () => { live = false; };
	}, [lines, slug, applied]);

	const setQuantity = (index: number, quantity: number) => {
		setLines(Basket.setQuantity(slug, index, quantity).slice());
	};

	const startPayment = async () => {
		if (!email.trim() || !email.includes("@")) {
			setError("We need an email address for your order.");
			return;
		}

		setStarting(true);
		setError(null);

		try {
			const res = await CheckoutService.createOrder({
				clubSlug: slug,
				// Stamps the order onto the account when there is one, so it
				// reaches their wallet on any device — and so per-person limits
				// count the person rather than the mailbox.
				fanId: FanSession.fanId(),
				guestEmail: email.trim(),
				guestName: name.trim() || null,
				lines: lines.map((l) => ({
					holders: l.holders || null,
					seatIds: l.seatIds || null,
					productId: l.productId,
					productType: l.productType,
					quantity: l.quantity,
					ticketTypeId: l.ticketTypeId || null
				})),
				promoCode: applied || null,
				ticketCategory: 0
			});

			if (!res.ok) {
				const body = await res.text();
				setStarting(false);
				setError(body?.replace(/^"|"$/g, "") || "We couldn't start that payment.");
				return;
			}

			const body = await res.json();
			setStarting(false);

			if (!body.clientSecret) {
				setError("We couldn't start that payment.");
				return;
			}
			// The key is how the confirmation page fetches the tickets. Stored
			// as well as passed, because a bank that takes over the page for 3DS
			// brings the fan back on a fresh load with only the URL to go on.
			try { window.localStorage.setItem(`pb-order-${body.orderId}`, body.orderKey); } catch { /* private browsing */ }
			setOrder({ clientSecret: body.clientSecret, orderId: body.orderId, orderKey: body.orderKey, totalPence: body.totalPence });
		} catch {
			setStarting(false);
			setError("We couldn't start that payment. Please try again.");
		}
	};

	if (!lines.length) {
		return (
			<div className="shop" style={clubTheme(club)}>
				<ShopHeader club={club} slug={slug} />
				<div className="shop-result">
					<h2>Your basket is empty</h2>
					<p>Nothing in here yet.</p>
					<button className="shop-btn" onClick={() => navigate(`/club/${slug}`)} type="button">
						See what&rsquo;s on
					</button>
				</div>
			</div>
		);
	}

	const totalPence = quote?.totalPence ?? Basket.subtotalPence(lines);

	return (
		<div className="shop" style={clubTheme(club)}>
			<ShopHeader club={club} slug={slug} />
			<div className="shop-inner">
				<h2 className="shop-section-title">Your basket</h2>

				<div className="shop-panel">
					{lines.map((line, index) => (
						<div className="shop-line" key={`${line.productType}-${line.productId}-${line.ticketTypeId}`}>
							<div className="shop-line-body">
								<div className="shop-line-title">{line.name}</div>
								<div className="shop-line-meta">
									{line.occasion}
									{line.when ? ` · ${new Date(line.when).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}
								</div>
							</div>
							{!order && <div className="shop-stepper">
								<button aria-label="One fewer" onClick={() => setQuantity(index, line.quantity - 1)} type="button">&minus;</button>
								<span>{line.quantity}</span>
								<button aria-label="One more" onClick={() => setQuantity(index, line.quantity + 1)} type="button">+</button>
							</div>}
							{order && <span>&times;{line.quantity}</span>}
							<span className="shop-ticket-price">{money(line.pricePence * line.quantity)}</span>
						</div>
					))}

					{quote?.discountPence > 0 &&
						<div className="shop-line">
							<div className="shop-line-body">
								<div className="shop-line-title">{quote.promoDescription || "Discount"}</div>
								<div className="shop-line-meta">Promo code applied</div>
							</div>
							<span className="shop-ticket-price shop-discount">&minus;{money(quote.discountPence)}</span>
						</div>}

					<div className="shop-total">
						<span>Total</span>
						<span>{money(totalPence)}</span>
					</div>
				</div>

				{error && <div className="shop-error">{error}</div>}

				{!order && <>
					<h2 className="shop-section-title">Promo code</h2>
					<div className="shop-panel">
						{applied && quote?.promoApplied
							? <div className="shop-promo-on">
								<span>{quote.promoDescription}</span>
								<button onClick={() => { setApplied(""); setPromo(""); }} type="button">Remove</button>
							</div>
							: <div className="shop-promo">
								<input
									autoCapitalize="characters"
									autoComplete="off"
									aria-label="Promo code"
									onChange={(e) => setPromo(e.target.value)}
									onKeyDown={(e) => { if (e.key === "Enter") setApplied(promo.trim()); }}
									placeholder="Got a code?"
									value={promo}
								/>
								<button className="shop-btn" disabled={!promo.trim()} onClick={() => setApplied(promo.trim())} type="button">
									Apply
								</button>
							</div>}
						{applied && quote && !quote.promoApplied && quote.promoError &&
							<p className="shop-promo-error">{quote.promoError}</p>}
					</div>

					<h2 className="shop-section-title">Your details</h2>
					<div className="shop-panel">
						<div className="shop-field">
							<label htmlFor="shop-name">Name</label>
							<input autoComplete="name" id="shop-name" onChange={(e) => setName(e.target.value)} value={name} />
						</div>
						<div className="shop-field">
							<label htmlFor="shop-email">Email</label>
							<input autoComplete="email" id="shop-email" inputMode="email" onChange={(e) => setEmail(e.target.value)} type="email" value={email} />
						</div>
						<p className="shop-note" style={{ textAlign: "left" }}>
							We&rsquo;ll use this to reach you about your order. Check it&rsquo;s right.
						</p>
					</div>

					{payment && !payment.canTakePayments &&
						<div className="shop-error">
							This club isn&rsquo;t set up to take online payments yet.
						</div>}

					<div className="shop-sticky">
						<button
							className="shop-btn"
							disabled={starting || (payment ? !payment.canTakePayments : false)}
							onClick={startPayment}
							type="button"
						>
							{starting ? "Just a moment…" : `Pay ${money(totalPence)}`}
						</button>
					</div>
				</>}

				{order && payment?.publishableKey &&
					<>
						<h2 className="shop-section-title">Payment</h2>
						<div className="shop-panel">
							<Elements
								options={{
									appearance: { theme: "night", variables: { colorPrimary: "#11B981" } },
									clientSecret: order.clientSecret
								}}
								stripe={stripeFor(payment.publishableKey)}
							>
								<PaymentForm orderId={order.orderId} orderKey={order.orderKey} slug={slug} totalPence={order.totalPence} />
							</Elements>
						</div>
					</>}

				{order && !payment?.publishableKey &&
					<div className="shop-error">
						Payments aren&rsquo;t configured for this club yet.
					</div>}
			</div>
		</div>
	);
}
